<template>
  <section class="impact-section" ref="section">
    <v-container>
      <!-- Section label -->
      <p class="impact-eyebrow">{{ $t('about.impactEyebrow') }}</p>

      <!-- Stats row -->
      <div class="impact-grid">
        <div
          v-for="(stat, i) in stats"
          :key="i"
          class="impact-stat"
          :class="{ 'is-visible': inView }"
          :style="{ transitionDelay: `${i * 0.15}s` }"
        >
          <div class="stat-number">
            <span class="stat-value">{{ stat.number }}</span>
            <span class="stat-suffix" :style="{ color: stat.color }">{{ stat.suffix }}</span>
          </div>
          <div class="stat-label">{{ $t(stat.labelKey) }}</div>
          <div class="stat-accent" :style="{ background: stat.color }" aria-hidden="true"></div>
        </div>
      </div>
    </v-container>
  </section>
</template>

<script>
export default {
  name: 'ImpactNumbers',
  data () {
    return {
      inView: false,
      stats: [
        { number: '100', suffix: '+', labelKey: 'about.impactSuccess', color: '#3949ab' },
        { number: '50',  suffix: '+', labelKey: 'about.impactPartners', color: '#7c3aed' },
        { number: '5',   suffix: '+', labelKey: 'about.impactYears',   color: '#e53935' }
      ]
    }
  },
  mounted () {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          this.inView = true
          observer.disconnect()
        }
      },
      { threshold: 0.25 }
    )
    observer.observe(this.$refs.section)
  }
}
</script>

<style lang="scss" scoped>
// ── Brand palette ─────────────────────────────────────────────────────
$blue:   #3949ab;
$purple: #7c3aed;
$red:    #e53935;

// ── Section ───────────────────────────────────────────────────────────
.impact-section {
  padding: 120px 0;
  background: #fafafa;

  .theme--dark & {
    background: #09090f;
  }
}

// ── Eyebrow ───────────────────────────────────────────────────────────
.impact-eyebrow {
  text-align: center;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: $blue;
  margin-bottom: 64px;

  .theme--dark & {
    color: lighten($blue, 28%);
  }

  // Arabic doesn't use uppercase; tracking breaks Arabic word shapes
  [dir='rtl'] & {
    letter-spacing: 0;
    text-transform: none;
  }
}

// ── Grid ──────────────────────────────────────────────────────────────
.impact-grid {
  display: flex;
  justify-content: center;
  align-items: stretch;
  max-width: 780px;
  margin: 0 auto;

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: center;
    gap: 56px;
    max-width: 280px;
  }
}

// ── Stat cell ─────────────────────────────────────────────────────────
.impact-stat {
  flex: 1;
  text-align: center;
  padding: 0 40px;
  position: relative;
  opacity: 0;
  transform: translateY(40px);
  transition: opacity 0.75s ease-out, transform 0.75s ease-out;

  &.is-visible {
    opacity: 1;
    transform: translateY(0);
  }

  // Divider between cells
  & + & {
    border-inline-start: 1px solid rgba(0, 0, 0, 0.08);

    .theme--dark & {
      border-inline-start-color: rgba(255, 255, 255, 0.08);
    }

    // Vuetify containers may not propagate dir attribute correctly;
    // explicitly swap the border side for RTL so the line stays between cells.
    [dir='rtl'] & {
      border-inline-start: none;
      border-inline-end: 1px solid rgba(0, 0, 0, 0.08);
    }

    [dir='rtl'] .theme--dark & {
      border-inline-end-color: rgba(255, 255, 255, 0.08);
    }
  }

  @media (max-width: 600px) {
    padding: 0;

    & + & {
      border-inline-start: none;
    }
  }
}

// ── Number ────────────────────────────────────────────────────────────
.stat-number {
  font-size: clamp(3.5rem, 9vw, 5.5rem);
  font-weight: 800;
  letter-spacing: -0.045em;
  line-height: 1;
  color: #1a1a2e;
  margin-bottom: 12px;

  .theme--dark & {
    color: #f0f0f0;
  }

  // Negative letter-spacing collapses Arabic glyphs
  [dir='rtl'] & {
    letter-spacing: 0;
  }
}

.stat-suffix {
  font-size: 0.5em;
  font-weight: 900;
  vertical-align: super;
  line-height: 0;
}

// ── Label ─────────────────────────────────────────────────────────────
.stat-label {
  font-size: 0.92rem;
  font-weight: 500;
  color: #777;
  letter-spacing: 0.01em;
  line-height: 1.4;

  .theme--dark & {
    color: #888;
  }
}

// ── Bottom accent bar ─────────────────────────────────────────────────
.stat-accent {
  width: 28px;
  height: 3px;
  border-radius: 2px;
  margin: 16px auto 0;
  opacity: 0.65;
}
</style>
